import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormControl, UntypedFormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NotificationType } from '@app/shared/interfaces';
import { Filter } from '@app/shared/models/filter';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { format } from 'date-fns';
import { BehaviorSubject, EMPTY, of } from 'rxjs';
import { catchError, filter, map, mergeMap, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { Agreement, AgreementAction, StatementStatus } from '../../../businesses/models/statements.models';
import { StatementsFacade } from '../../../businesses/services/statements.facade';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { DialogNewOwnerComponent } from '../../components/dialog-new-owner/dialog-new-owner.component';
import { DialogNewAgreementComponent } from '@app/modules/statements/components/dialog-new-agreement/dialog-new-agreement.component';

const buildPropertyFilter = (id: number): Filter => ({
  type: 'static',
  id: 'os-properties',
  value: id,
  label: null,
  lockable: false,
});

const buildStartsAtOptions = (nextOpenPeriodAt: string) => {
  return Array.from({ length: 12 }, (_, i) => {
    const nextOpenPeriod = new Date(`${nextOpenPeriodAt}T00:00:00.000`);
    nextOpenPeriod.setMonth(nextOpenPeriod.getMonth() + i);
    const formatted = format(nextOpenPeriod, 'YYYY-MM-DD');
    return {
      label: formatted,
      value: formatted,
    };
  });
};

@Component({
  standalone: false,
  selector: 'sbnb-property',
  templateUrl: './property.component.html',
  styleUrls: ['./property.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyComponent implements OnInit {
  public data$ = this.dataSource;

  // Includes the new commission rate item when the "Add Agreement" form is visible
  public commissionsDataSource$ = new BehaviorSubject<Partial<Agreement>[]>([]);

  public commissionColumns = ['name', 'startsAt', 'endsAt', 'actions'];
  public addingCommissionPeriod$ = new BehaviorSubject<boolean>(false);
  public newCommissionPeriodForm = this.fb.group({
    id: ['', [Validators.required]],
    startsAt: ['', [Validators.required]],
  });
  public savingCommissionPeriod$ = new BehaviorSubject<boolean>(false);
  public availableCommissions$ = this.statementsService.commissionState$.pipe(
    filter((state) => Boolean(state.data)),
    map((state) => state.data),
    mergeMap((availableCommissions) =>
      this.commissionsDataSource$.pipe(
        map((commissions) => {
          const currentlyAssigned = commissions.find((commission) => commission.id);

          return availableCommissions
            .filter((commission) => !currentlyAssigned || commission.id !== currentlyAssigned.id)
            .map((commission) => ({
              label: commission.name,
              value: commission.id,
            }));
        })
      )
    )
  );
  public ownerCtrl = new FormControl<number>(null);
  public statementColumns = ['month', 'type', 'property', 'owner'];
  public propertyId: number;
  public nextOpenPeriodAt$ = new BehaviorSubject<string>(null);
  public startsAtOptions$ = this.nextOpenPeriodAt$.pipe(map(buildStartsAtOptions));
  public agreementActionEnum = AgreementAction;
  public owners$ = this.statementsService.ownersState$.pipe(
    map((state) => state.data?.map((owner) => ({ label: `${owner.firstName} ${owner.name}`, value: owner.id })))
  );
  public savingOwner$ = new BehaviorSubject<boolean>(false);
  public closedStatements$ = this.statementsService.closedStatementsState$.pipe(
    map((state) => state.data),
    tap((state) => {
      if (state?.length) {
        this.ownerCtrl.disable();
      }
    })
  );

  constructor(
    private readonly route: ActivatedRoute,
    private readonly statementsService: StatementsFacade,
    private readonly fb: UntypedFormBuilder,
    private readonly router: Router,
    private toast: ToastNotificationsService,
    private readonly dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.propertyId = Number(this.route.snapshot.params['id']);
    // Statements for the property
    this.statementsService.getStatements(StatementStatus.Draft, [buildPropertyFilter(this.propertyId)]);
    this.statementsService.getStatements(StatementStatus.InReview, [buildPropertyFilter(this.propertyId)]);
    this.statementsService.getStatements(StatementStatus.Published, [buildPropertyFilter(this.propertyId)]);
    this.statementsService.getCommissionAgreements();
    this.statementsService.getOwners();
  }

  public addCommissionPeriod(): void {
    this.nextOpenPeriodAt$.pipe(take(1)).subscribe((nextOpenPeriodAt) => {
      this.addingCommissionPeriod$.next(true);

      const newStart = nextOpenPeriodAt ?? format(new Date(), 'YYYY-MM-DD');
      this.newCommissionPeriodForm.setControl('startsAt', this.fb.control(newStart));
      this.commissionsDataSource$.next([
        { id: null, starts_at: newStart, ends_at: null },
        ...this.commissionsDataSource$.value,
      ]);
      this.statementsService.getCommissionAgreements();
    });
  }

  public newDateSelected(event: any): void {
    this.newCommissionPeriodForm.get('startsAt').setValue(event);
  }

  public saveCommissionPeriod(): void {
    this.savingCommissionPeriod$.next(true);
    this.statementsService
      .addPropertyCommissionRate({
        propertyId: this.propertyId,
        commissionRateId: this.newCommissionPeriodForm.value.id,
        startsAt: this.newCommissionPeriodForm.value.startsAt,
      })
      .pipe(
        catchError((err) => {
          this.cancelAddCommissionPeriod();
          throw err;
        })
      )
      .subscribe(() => {
        this.resetForm();
        this.data$ = this.dataSource;
      });
  }

  public cancelAddCommissionPeriod(): void {
    this.resetForm();
    if (this.commissionsDataSource$.value.length === 1) {
      this.commissionsDataSource$.next([]);
      return;
    }
    const mostRecentCommission = { ...this.commissionsDataSource$.value[1], ends_at: null };
    this.commissionsDataSource$.next([{ ...mostRecentCommission }, ...this.commissionsDataSource$.value.slice(2)]);
  }

  public deletePropertyCommissionRate(id: Agreement['id']): void {
    this.savingCommissionPeriod$.next(true);
    this.statementsService
      .deletePropertyCommissionRate({
        propertyId: this.propertyId,
        commissionRateId: id,
      })
      .subscribe(() => {
        this.resetForm();
        this.data$ = this.dataSource;
      });
  }

  public changeOwner(): void {
    this.savingOwner$.next(true);
    this.statementsService.ownersState$
      .pipe(
        take(1),
        switchMap((state) => {
          const previousOwner = state.data.find((owner) =>
            owner.properties.some((property) => property.id === this.propertyId)
          );

          if (previousOwner) {
            const previousOwnerPropertyIds = previousOwner.properties
              .map((property) => property.id)
              .filter((id) => id !== this.propertyId);
            return this.statementsService
              .updateOwner(previousOwner, true, previousOwnerPropertyIds)
              .pipe(switchMap(() => this.statementsService.ownersState$.pipe(take(1))));
          } else {
            return of(state);
          }
        }),
        switchMap((state) => {
          const newOwner = state.data.find((owner) => owner.id === this.ownerCtrl.value);
          const newOwnerPropertyIds = newOwner.properties.map((property) => property.id);

          return this.statementsService.updateOwner(newOwner, true, [...newOwnerPropertyIds, this.propertyId]);
        })
      )
      .subscribe((owner) => {
        this.savingOwner$.next(false);
        this.ownerCtrl.reset(owner?.id);
      });
  }

  public openNewOwnerDialog(): void {
    this.dialog
      .open(DialogNewOwnerComponent)
      .afterClosed()
      .subscribe(() => {});
  }

  public openNewAgreementDialog(): void {
    this.dialog
      .open(DialogNewAgreementComponent, {
        maxHeight: '90vh',
        maxWidth: '90vw',
      })
      .afterClosed()
      .subscribe(() => {});
  }

  private resetForm(): void {
    this.newCommissionPeriodForm.reset();
    this.addingCommissionPeriod$.next(false);
    this.savingCommissionPeriod$.next(false);
  }

  private sortCommissionRates(commissionRates: Omit<Agreement, 'properties'>[]) {
    const sorted = commissionRates?.sort((a, b) => {
      if (!a.ends_at) return -1;
      if (!b.ends_at) return 1;
      return new Date(a.ends_at) > new Date(b.ends_at) ? -1 : 1;
    });

    this.commissionsDataSource$.next(sorted);
  }

  private get dataSource() {
    return this.statementsService.getProperty(this.route.snapshot.params['id']).pipe(
      filter((property) => Boolean(property)),
      tap((property) => this.sortCommissionRates(property.commissionRates)),
      tap((property) => this.nextOpenPeriodAt$.next(property.nextOpenPeriodAt)),
      tap((property) => this.ownerCtrl.setValue(property.owners[0]?.id)),
      shareReplay(1),
      catchError((err) => {
        this.toast.open('Unable to to find this owner statement property.', 'Dismiss', NotificationType.Error);
        this.router.navigate(['../../setup'], { relativeTo: this.route });
        return EMPTY;
      })
    );
  }
}
